import { BasePayloadMapper } from '@ps/utils';
import { BuyerSettingsUpdatePayloadDto } from '../dtos/buyer.dto';
import { BuyerSettings, VariableMapping } from '../../state/buyer-state.model';

const convertBooleanToNumber = (enabled: boolean): number => (enabled ? 1 : 0);
const ST_ACTIVE = 1;

class BuyerVariableMappingReader {
  private readonly _map: Map<string, VariableMapping>;

  constructor(variableMapping: VariableMapping[]) {
    this._map = new Map(
      variableMapping.map((variableMappingItem) => [
        variableMappingItem.key,
        variableMappingItem,
      ]),
    );
  }

  public get(key: string): {
    enabled: boolean;
    outgoing: string;
    incoming: string;
  } {
    const variableMappingItem = this._map.get(key);
    return {
      enabled: !!variableMappingItem,
      outgoing: variableMappingItem?.outgoing ?? '',
      incoming: variableMappingItem?.incoming ?? '',
    };
  }

  public toPayload(): {
    [key: string]: { outgoing: string; incoming: string };
  }[] {
    return Array.from(this._map.values()).map((variableMappingItem) => ({
      [variableMappingItem.key]: {
        outgoing: variableMappingItem.outgoing,
        incoming: variableMappingItem.incoming,
      },
    }));
  }
}

export class BuyerSettingsPayloadMapper extends BasePayloadMapper<
  BuyerSettings,
  BuyerSettingsUpdatePayloadDto
> {
  public override toPayload(
    settings: BuyerSettings,
  ): BuyerSettingsUpdatePayloadDto {
    const variableMap = new BuyerVariableMappingReader(
      settings.variableMapping,
    );

    return {
      cmp: settings.cmp,
      cmpName: settings.companyName,
      decipherObj: {
        cmp: null,
        directory_name: '',
        locale: '',
        selectedQualification: null,
        selectedSupplierType: '',
        selectedURI: '',
        vendor: '',
      },
      private_key: settings.privateKey,
      ...(settings.decipherHashKeyId !== null
        ? { decipherHashKeyID: settings.decipherHashKeyId }
        : {}),
      hashType: settings.hashType || '',
      isSHA1: settings.hashType === 'sha1',
      isSHA2: settings.hashType === 'sha2',
      isOutboundHash: settings.isOutboundHash,

      transactionIdCb: convertBooleanToNumber(
        variableMap.get('ps_transaction').enabled,
      ),
      transactionIdIn: variableMap.get('ps_transaction').incoming,
      transactionIdOut: variableMap.get('ps_transaction').outgoing,

      psTransactionId2Cb: convertBooleanToNumber(
        variableMap.get('ps_transaction_id2').enabled,
      ),
      psTransactionId2In: variableMap.get('ps_transaction_id2').incoming,
      psTransactionId2Out: variableMap.get('ps_transaction_id2').outgoing,

      psTransactionId3Cb: convertBooleanToNumber(
        variableMap.get('ps_transaction_id3').enabled,
      ),
      psTransactionId3In: variableMap.get('ps_transaction_id3').incoming,
      psTransactionId3Out: variableMap.get('ps_transaction_id3').outgoing,

      psTransactionId4Cb: convertBooleanToNumber(
        variableMap.get('ps_transaction_id4').enabled,
      ),
      psTransactionId4In: variableMap.get('ps_transaction_id4').incoming,
      psTransactionId4Out: variableMap.get('ps_transaction_id4').outgoing,

      psIdCb: convertBooleanToNumber(variableMap.get('ps_psid').enabled),
      psIdOut: variableMap.get('ps_psid').outgoing,
      psIdIn: variableMap.get('ps_psid').incoming,

      psSurveyIdCb: convertBooleanToNumber(
        variableMap.get('ps_survey_id').enabled,
      ),
      psSurveyIdOut: variableMap.get('ps_survey_id').outgoing,

      psSupplierIdCb: convertBooleanToNumber(
        variableMap.get('ps_supplier_id').enabled,
      ),
      psSupplierIdOut: variableMap.get('ps_supplier_id').outgoing,

      psSupplierGroupNameCb: convertBooleanToNumber(
        variableMap.get('ps_supplier_group_name').enabled,
      ),
      psSupplierGroupNameOut: variableMap.get('ps_supplier_group_name')
        .outgoing,

      statusCb: convertBooleanToNumber(variableMap.get('ps_status').enabled),
      statusIn: variableMap.get('ps_status').incoming,

      psHashCb: convertBooleanToNumber(variableMap.get('ps_hash').enabled),
      psHashIn: variableMap.get('ps_hash').incoming,

      psHashedIdCb: convertBooleanToNumber(
        variableMap.get('ps_hashed_id').enabled,
      ),
      psHashedIdOut: variableMap.get('ps_hashed_id').outgoing,
      psHashedIdIn: variableMap.get('ps_hashed_id').incoming,

      decipherHashIn: '',
      decipherHashOut: '',

      variable_mapping: variableMap.toPayload(),

      isHashing: settings.isHashing,
      isDecipher: settings.isDecipher,
      st: ST_ACTIVE,
      is_decipher_hash: settings.isDecipherHash,
      consolidatedView: settings.consolidatedView,
    };
  }
}
